import { ExternalLink, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@/components/ui';
import { toast } from '@/lib/toast';
import { useDeleteProject, useLinkLinear, useUnlinkLinear, useStudioClient, useStudioMe, useStudioTeam, useUpdateProject } from '@/api/studio';
import type { Covers, Milestone, Project, ProjectPatch } from '@/api/types';
import { coversLabel, invoiceDate, nextPeriodOf, periodLabel } from '@/lib/billing';
import { formatDate } from '@/lib/format';
import { LinearProjectPicker } from './LinearProjectPicker';
import { RhythmEditor } from './RhythmEditor';
import { studioCan } from '@/lib/access';

type Status = NonNullable<ProjectPatch['status']>;
const statusLabel: Record<Status, string> = { active: 'Идёт', paused: 'На паузе', done: 'Завершён' };
const statusOf = (p: Project): Status => (p.health === 'paused' ? 'paused' : p.health === 'done' ? 'done' : 'active');
const msStates: Record<Milestone['state'], string> = { done: 'готов', current: 'идёт', next: 'впереди' };

type Row = { id?: string; title: string; due: string; state: Milestone['state'] };

const today = () => new Date().toISOString().slice(0, 10);

/** The project card's own fields: what was only reachable in the database before (MK42-5831). */
export function ProjectSettings({ p }: { p: Project }) {
  const update = useUpdateProject();
  const { data: team } = useStudioTeam();
  const me = useStudioMe();
  const money = studioCan(me?.studioRole, 'money');
  const [title, setTitle] = useState(p.title);
  const [summary, setSummary] = useState(p.summary);
  const [status, setStatus] = useState<Status>(statusOf(p));
  const [ownerId, setOwnerId] = useState(p.owner.id);
  const [teamIds, setTeamIds] = useState(p.team.map((x) => x.id));
  const [finishBy, setFinishBy] = useState(p.kind === 'fixed' ? p.finishBy : '');
  const [until, setUntil] = useState(p.terms.until ?? '');
  // Who the project's invoices are made out to: '' — the client's default payer.
  const payers = useStudioClient(p.clientId).data?.payers ?? [];
  const [payerId, setPayerId] = useState(p.payerId ?? '');
  const [price, setPrice] = useState(String(p.terms.price.amountMinor / 100));
  const retainer = p.kind === 'retainer' ? p.terms : null;
  const [billDay, setBillDay] = useState(String(retainer ? (retainer.billingDay ?? Number(retainer.nextInvoiceAt.slice(8, 10))) : 1));
  const [covers, setCovers] = useState<Covers>(retainer?.billingCovers ?? 'current');
  const [period, setPeriod] = useState(retainer ? nextPeriodOf(retainer) : '');
  const billingValid = /^\d{4}-\d{2}$/.test(period) && Number.isInteger(Number(billDay)) && Number(billDay) >= 1 && Number(billDay) <= 28;
  const [rows, setRows] = useState<Row[]>(p.kind === 'fixed' ? p.milestones.map((m) => ({ id: m.id, title: m.title, due: m.due ?? '', state: m.state })) : []);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  const setRow = (i: number, v: Partial<Row>) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, ...v } : x)));

  function save() {
    const patch: ProjectPatch = {};
    if (title.trim() !== p.title) patch.title = title.trim();
    if (summary !== p.summary) patch.summary = summary;
    if (status !== statusOf(p)) patch.status = status;
    if (ownerId !== p.owner.id) patch.ownerId = ownerId;
    const was = p.team.map((x) => x.id).sort().join();
    if ([...new Set([ownerId, ...teamIds])].sort().join() !== was) patch.teamIds = teamIds;
    if (p.kind === 'fixed') {
      if (finishBy && finishBy !== p.finishBy) patch.finishBy = finishBy;
      const next = rows.filter((r) => r.title.trim()).map((r) => ({ id: r.id, title: r.title.trim(), due: r.due || null, state: r.state }));
      const before = p.milestones.map((m) => ({ id: m.id, title: m.title, due: m.due ?? null, state: m.state }));
      if (JSON.stringify(next) !== JSON.stringify(before)) patch.milestones = next;
    }
    if (money) {
      const minor = Math.round(parseFloat(price.replace(',', '.')) * 100);
      if (Number.isFinite(minor) && minor !== p.terms.price.amountMinor) patch.price = { amountMinor: minor, currency: p.terms.price.currency };
      if (until !== (p.terms.until ?? '')) patch.until = until || null;
      if (payerId !== (p.payerId ?? '')) patch.payerId = payerId || null;
      if (p.kind === 'retainer' && billingValid) {
        const b = { day: Number(billDay), covers, nextPeriod: period };
        const was = { day: p.terms.billingDay ?? Number(p.terms.nextInvoiceAt.slice(8, 10)), covers: p.terms.billingCovers ?? 'current', nextPeriod: nextPeriodOf(p.terms) };
        if (JSON.stringify(b) !== JSON.stringify(was)) patch.billing = b;
      }
    }
    setSaved(false);
    update.mutate(
      { id: p.id, patch },
      {
        onSuccess: (r) => {
          setWarnings(r.warnings ?? []);
          setSaved(true);
        },
      },
    );
  }

  const people = team?.filter((m) => !m.disabled) ?? [];
  const toggle = (id: string) => setTeamIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  return (
    <div className="space-y-10">
      <form
        className="frame space-y-4 px-6 py-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) save();
        }}
      >
        <h2 className="text-sm font-semibold">Карточка проекта</h2>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Название</span>
          <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Название" />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Описание</span>
          <textarea className="field min-h-20" value={summary} onChange={(e) => setSummary(e.target.value)} aria-label="Описание" />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Статус</span>
            <select className="field" value={status} onChange={(e) => setStatus(e.target.value as Status)} aria-label="Статус">
              {(Object.keys(statusLabel) as Status[]).map((k) => (
                <option key={k} value={k}>
                  {statusLabel[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Ведёт</span>
            <select className="field" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} aria-label="Ведёт">
              {people.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="text-sm">
          <span className="mb-1.5 block text-muted">Команда</span>
          <div className="flex flex-wrap gap-1.5">
            {people.map((m) => (
              <button type="button" key={m.id} className="chip" aria-pressed={m.id === ownerId || teamIds.includes(m.id)} disabled={m.id === ownerId} onClick={() => toggle(m.id)}>
                {m.name}
              </button>
            ))}
          </div>
        </div>
        {p.kind === 'fixed' && (
          <label className="block text-sm">
            <span className="mb-1.5 block text-muted">Сдача проекта</span>
            <input className="field" type="date" value={finishBy} onChange={(e) => setFinishBy(e.target.value)} aria-label="Сдача проекта" />
          </label>
        )}
        {money && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1.5 block text-muted">{p.kind === 'fixed' ? 'Цена' : 'Цена за месяц'}, {p.terms.price.currency === 'RUB' ? '₽' : p.terms.price.currency}</span>
              <input className="field" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} aria-label="Цена" />
            </label>
            <label className="block text-sm">
              <span className="mb-1.5 block text-muted">Договор до</span>
              <input className="field" type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="Договор до" />
            </label>
            {payers.length > 1 && (
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1.5 block text-muted">Плательщик по счетам проекта</span>
                <select className="field" value={payerId} onChange={(e) => setPayerId(e.target.value)} aria-label="Плательщик">
                  <option value="">Основной: {payers.find((x) => x.isDefault)?.legalName || 'не указан'}</option>
                  {payers
                    .filter((x) => !x.isDefault || payerId === x.id)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.legalName || 'Название не указано'}
                        {x.missing.length ? ` (нет: ${x.missing.join(', ')})` : ''}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>
        )}
        {money && p.kind === 'retainer' && (
          <fieldset className="space-y-3 text-sm">
            <legend className="mb-1.5 text-muted">Счета</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[8rem_1fr_10rem]">
              <label className="block">
                <span className="mb-1.5 block text-muted">Число месяца</span>
                <input className="field" type="number" min={1} max={28} value={billDay} onChange={(e) => setBillDay(e.target.value)} aria-label="Число месяца" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-muted">Счёт выставляется</span>
                <select className="field" value={covers} onChange={(e) => setCovers(e.target.value as Covers)} aria-label="Счёт выставляется">
                  {(Object.keys(coversLabel) as Covers[]).map((k) => (
                    <option key={k} value={k}>
                      {coversLabel[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-muted">Следующий счёт за</span>
                <input className="field" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Следующий счёт за" />
              </label>
            </div>
            {billingValid && (
              <p className="text-muted">
                Следующий счёт — {formatDate(invoiceDate(period, covers, Number(billDay)))}, за {periodLabel(period)}
                {invoiceDate(period, covers, Number(billDay)) <= today() ? ': дата уже наступила, счёт уйдёт сразу после сохранения' : ''}.
                {p.terms.lastInvoicedPeriod ? ` Последний выставленный — за ${periodLabel(p.terms.lastInvoicedPeriod)}.` : ''}
              </p>
            )}
          </fieldset>
        )}
        {p.kind === 'fixed' && (
          <fieldset className="space-y-2 text-sm">
            <legend className="mb-1.5 text-muted">Этапы</legend>
            {rows.map((r, i) => (
              <div key={r.id ?? `new-${i}`} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
                <input className="field" value={r.title} onChange={(e) => setRow(i, { title: e.target.value })} aria-label={`Этап ${i + 1}`} />
                <input className="field" type="date" value={r.due} onChange={(e) => setRow(i, { due: e.target.value })} aria-label={`Срок этапа ${i + 1}`} />
                <select className="field" value={r.state} onChange={(e) => setRow(i, { state: e.target.value as Milestone['state'] })} aria-label={`Состояние этапа ${i + 1}`}>
                  {(Object.keys(msStates) as Milestone['state'][]).map((k) => (
                    <option key={k} value={k}>
                      {msStates[k]}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-ghost size-8 px-0" aria-label={`Убрать этап ${i + 1}`} onClick={() => setRows((xs) => xs.filter((_, j) => j !== i))}>
                  <X className="size-4" />
                </button>
              </div>
            ))}
            <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => setRows((xs) => [...xs, { title: '', due: '', state: 'next' }])}>
              <Plus className="size-3.5" /> Добавить этап
            </button>
          </fieldset>
        )}
        {update.error && (
          <p role="alert" className="text-sm text-overdue">
            {(update.error as Error).message}
          </p>
        )}
        {warnings.map((w) => (
          <p key={w} className="text-sm text-waiting">
            {w}
          </p>
        ))}
        {saved && !update.error && <p className="text-sm text-done">Сохранено</p>}
        <button className="btn-primary" disabled={!title.trim() || update.isPending}>
          Сохранить
        </button>
      </form>

      <RhythmEditor p={p} />
      <section className="frame space-y-3 px-6 py-6">
        <LinearLink p={p} />
      </section>
      {money && <DangerZone p={p} />}
    </div>
  );
}

/** «Удалить проект»: for a project made by mistake. The reason a real one cannot go comes from the server. */
function DangerZone({ p }: { p: Project }) {
  const del = useDeleteProject();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const close = () => {
    setOpen(false);
    setTyped('');
    del.reset();
  };
  const confirmed = typed.trim() === p.title.trim();

  return (
    <section className="frame space-y-3 border-overdue/40 px-6 py-6" aria-labelledby="danger-zone">
      <h2 id="danger-zone" className="text-sm font-semibold text-overdue">
        Опасная зона
      </h2>
      <p className="text-sm text-muted">
        Удаление стирает проект со всеми задачами-пометками, перепиской и файлами в кабинете. Проект в Linear остаётся. Проект со счетами, актами или подписанными документами удалить нельзя — его завершают.
      </p>
      <button type="button" className="btn-ghost text-overdue" onClick={() => setOpen(true)}>
        Удалить проект
      </button>
      <Dialog open={open} onClose={close} title="Удалить проект?">
        <form
          className="space-y-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!confirmed || del.isPending) return;
            del.mutate(p.id, {
              onSuccess: () => {
                toast('Проект удалён');
                navigate(`/studio/clients/${p.clientId}`);
              },
            });
          }}
        >
          <p className="text-sm">
            Проект «{p.title}» исчезнет у клиента и у студии, вернуть его нельзя. Чтобы подтвердить, введите название проекта.
          </p>
          <label className="block space-y-1 text-sm">
            <span className="text-muted">Название проекта</span>
            <input className="field" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" aria-label="Название проекта для подтверждения" data-autofocus />
          </label>
          {del.error && (
            <p role="alert" className="text-sm text-overdue">
              {(del.error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={close}>
              Отмена
            </button>
            <button className="btn-primary bg-overdue" disabled={!confirmed || del.isPending}>
              Удалить проект
            </button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}

/**
 * Linear for the project: link one (found by name or created), switch to another, or unlink.
 * Switching or unlinking changes only the portal: nothing is deleted in Linear.
 */
function LinearLink({ p }: { p: Project }) {
  const link = useLinkLinear();
  const unlink = useUnlinkLinear();
  const [editing, setEditing] = useState(!p.linear);
  const [name, setName] = useState(p.linear ? '' : p.title);
  const [confirming, setConfirming] = useState(false);
  const error = (link.error ?? unlink.error) as Error | null;
  return (
    <>
      <h2 className="text-sm font-semibold">Linear</h2>
      {p.linear && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <span>
            Связан с проектом «{p.linear.name}»{' '}
            <a className="inline-flex items-center gap-1 text-muted hover:text-ink" href={p.linear.url} target="_blank" rel="noreferrer">
              открыть <ExternalLink className="size-3.5" />
            </a>
          </span>
          {!editing && (
            <span className="ml-auto flex gap-2">
              <button type="button" className="btn-secondary h-8" onClick={() => setEditing(true)}>
                Сменить проект
              </button>
              <button type="button" className="btn-ghost h-8" onClick={() => setConfirming(true)}>
                Отвязать
              </button>
            </span>
          )}
        </div>
      )}
      {editing && (
        <>
          <p className="text-sm text-muted">
            {p.linear
              ? 'Выберите другой проект Linear: задачи в кабинете будут браться из него. В Linear ничего не удаляется.'
              : 'Проект не связан с Linear, поэтому задач в нём пока нет. Укажите название: если проект с таким именем есть, он подключится, иначе будет создан.'}
          </p>
          <div className="flex gap-2">
            <div className="flex-1">
              <LinearProjectPicker value={name} onChange={setName} ariaLabel="Проект в Linear" />
            </div>
            <button
              className="btn-primary shrink-0"
              disabled={!name.trim() || link.isPending}
              onClick={() => link.mutate({ projectId: p.id, name: name.trim() }, { onSuccess: () => (setEditing(false), setName('')) })}
            >
              {p.linear ? 'Привязать' : 'Связать с Linear'}
            </button>
            {p.linear && (
              <button type="button" className="btn-ghost shrink-0" onClick={() => setEditing(false)}>
                Отмена
              </button>
            )}
          </div>
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-overdue">
          {error.message}
        </p>
      )}
      <Dialog open={confirming} onClose={() => setConfirming(false)} title="Отвязать от Linear?">
        <div className="space-y-4 p-5">
          <p className="text-sm">Задачи проекта пропадут из кабинета у вас и у клиента, пока проект не связан снова. В Linear ничего не удаляется.</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setConfirming(false)}>
              Отмена
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={unlink.isPending}
              onClick={() =>
                unlink.mutate(p.id, {
                  onSuccess: () => {
                    setConfirming(false);
                    setEditing(true);
                    setName(p.title);
                  },
                })
              }
            >
              Отвязать
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
